import { useState } from "react";
import { Command as CommandPrimitive } from "cmdk";
import { Check, ChevronsUpDown, Search, UserPlus } from "lucide-react";

import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

type Accent = "emerald" | "purple";

const ACCENT: Record<Accent, { trigger: string; check: string }> = {
  emerald: {
    trigger:
      "hover:border-emerald-500/60 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 data-[state=open]:border-emerald-500 data-[state=open]:ring-2 data-[state=open]:ring-emerald-500/20",
    check: "text-emerald-600",
  },
  purple: {
    trigger:
      "hover:border-purple-500/60 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 data-[state=open]:border-purple-500 data-[state=open]:ring-2 data-[state=open]:ring-purple-500/20",
    check: "text-purple-600",
  },
};

interface ClienteComboboxProps {
  options: { label: string; value: string }[];
  value?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  accent?: Accent;
  /** Campo opcional: sem asterisco e com a opção "Sem cliente" para limpar a escolha. */
  optional?: boolean;
  /** Texto da opção que limpa a escolha, quando `optional`. */
  optionalLabel?: string;
  error?: string;
  /** Quando informado, o rodapé da lista oferece pedir o cadastro de um cliente que não existe. */
  onPedirCadastro?: (nomeDigitado: string) => void;
}

/**
 * Seletor de cliente com busca. Lista carregada client-side, então o cmdk filtra
 * sozinho. Popover NÃO-modal dentro de um Dialog: um Popover modal aninhado trava
 * `pointer-events` no body ao fechar. Mesma UI do {@link PessoaCombobox}.
 */
export function ClienteCombobox({
  options,
  value,
  onChange,
  disabled,
  label = "Selecione o Cliente",
  placeholder = "Buscar cliente...",
  accent = "emerald",
  optional = false,
  optionalLabel = "Sem cliente",
  error,
  onPedirCadastro,
}: Readonly<ClienteComboboxProps>) {
  const [open, setOpen] = useState(false);
  const [busca, setBusca] = useState("");
  const selected = options.find((o) => o.value === value);
  const cores = ACCENT[accent];

  return (
    <div className="space-y-2">
      <Label className="text-sm font-semibold">
        {label} {!optional && <span className="text-destructive">*</span>}
      </Label>
      <Popover
        open={open}
        onOpenChange={(aberto) => {
          setOpen(aberto);
          if (!aberto) setBusca("");
        }}
      >
        <PopoverTrigger
          disabled={disabled}
          className={cn(
            "flex h-11 w-full items-center justify-between rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition-colors",
            cores.trigger,
            disabled && "cursor-not-allowed opacity-50",
          )}
        >
          <span className={cn("truncate", !selected && "text-muted-foreground")}>
            {selected ? selected.label : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-muted-foreground" />
        </PopoverTrigger>

        {/* pointer-events-auto + stopPropagation: dentro de um Dialog modal o body
            fica com pointer-events:none; sem isto o conteúdo portalado não recebe clique. */}
        <PopoverContent
          className="pointer-events-auto w-[var(--radix-popover-trigger-width)] p-0"
          align="start"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <CommandPrimitive className="flex w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground">
            <div className="flex items-center gap-2 border-b px-3">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
              <CommandPrimitive.Input
                value={busca}
                onValueChange={setBusca}
                placeholder={placeholder}
                className="h-10 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
            </div>

            <CommandPrimitive.List
              className="max-h-72 overflow-y-auto overflow-x-hidden p-1"
              // Dentro de um Dialog modal, o scroll-lock do Radix (react-remove-scroll)
              // dá preventDefault no wheel de conteúdo portalado fora do dialog e trava
              // a rolagem. Rolamos na mão — preventDefault não impede setar scrollTop.
              // ponytail: só necessário sob Dialog modal; fora dele rolaria em dobro.
              onWheel={(e) => {
                e.currentTarget.scrollTop += e.deltaY;
              }}
            >
              <CommandPrimitive.Empty className="py-6 text-center text-sm text-muted-foreground">
                Nenhum cliente encontrado.
              </CommandPrimitive.Empty>

              {optional && (
                <CommandPrimitive.Item
                  value={optionalLabel}
                  onSelect={() => {
                    onChange("");
                    setOpen(false);
                  }}
                  className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm italic text-muted-foreground outline-none data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground"
                >
                  <Check className="h-4 w-4 shrink-0 opacity-0" />
                  {optionalLabel}
                </CommandPrimitive.Item>
              )}

              {options.map((option) => {
                const selecionado = option.value === value;
                return (
                  <CommandPrimitive.Item
                    key={option.value}
                    value={option.label}
                    onSelect={() => {
                      onChange(option.value);
                      setOpen(false);
                    }}
                    className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground"
                  >
                    <Check
                      className={cn(
                        "h-4 w-4 shrink-0",
                        cores.check,
                        selecionado ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <span className="truncate">{option.label}</span>
                  </CommandPrimitive.Item>
                );
              })}
            </CommandPrimitive.List>

            {onPedirCadastro && (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onPedirCadastro(busca);
                }}
                className="flex w-full items-center gap-2 border-t px-3 py-2.5 text-left text-sm text-primary outline-none hover:bg-accent focus-visible:bg-accent"
              >
                <UserPlus className="h-4 w-4 shrink-0" />
                <span>
                  Não achou o cliente? <strong>Pedir o cadastro</strong>
                </span>
              </button>
            )}
          </CommandPrimitive>
        </PopoverContent>
      </Popover>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
